/**
 * workshopCreateValidation — per-step required-field validation for the Create
 * Workshop wizard (BON-203 B1/B2). Pure functions: each returns a `StepErrors`
 * map (empty when valid) keyed to the inline-error keys the step bodies render.
 * Extracted from useWorkshopCreateForm to keep that file under the size cap.
 */
import type {
  WorkshopDraft,
  WorkshopDraftSchedule,
  WorkshopDraftWorkshop,
} from './useWorkshopCreateForm';
import { recurrenceFieldErrors, seriesDatesError, seriesProblem } from './workshopCreateSeries';
import { isPastDate, PAST_DATE_MESSAGE } from './workshopLeadTime';
import { PAST_MIDNIGHT_MESSAGE } from './workshopTypeDefaults';
import { toIso } from './workshopWallClock';

// Still imported from here by the hook and the submit path.
export { toIso };

/** Per-field error map for a step ({} when valid). */
export type StepErrors = Record<string, string>;

export const NO_LENGTH_MESSAGE =
  'This workshop type has no length set. Add one in Settings › Workshop types.';

/** Step 1 — workshop type (required), capacity (required positive integer),
 *  ticket price (required, non-negative amount). */
function workshopStepErrors(w: WorkshopDraftWorkshop): StepErrors {
  const errs: StepErrors = {};
  if (w.workshopTypeId === '') errs.workshopTypeId = 'Pick a workshop type.';
  else if (w.durationMinutes === null) errs.workshopTypeId = NO_LENGTH_MESSAGE;
  const cap = Number(w.capacity);
  if (w.capacity.trim() === '') errs.capacity = 'Capacity is required.';
  else if (!Number.isInteger(cap) || cap < 1) errs.capacity = 'Enter a whole number of seats (1+).';
  const price = Number(w.price);
  if (w.price.trim() === '') errs.priceCents = 'Ticket price is required.';
  else if (!Number.isFinite(price) || price < 0) errs.priceCents = 'Enter a valid price (0+).';
  return errs;
}

/** Location step (round 8) — region + venue BOTH required to leave step 1. The
 *  field MODE doesn't relax this: a `fixed`/`prefill` field is seeded, so it
 *  passes; an `open`/cleared one surfaces an inline error. */
function locationStepErrors(draft: WorkshopDraft): StepErrors {
  const errs: StepErrors = {};
  if (draft.regionId === '') errs.regionId = 'Select a region.';
  if (draft.schedule.venueId === '') errs.venueId = 'Select a venue.';
  return errs;
}

/** Schedule step (round 8) — date and start (venue moved to Location; the end
 *  follows from the type's length); the date is not in the past. The 6-week lead is deliberately
 *  NOT a step error (BON-508) — it warns in the UI but never blocks Continue.
 *  A repeating schedule also needs its end condition filled in, a series the
 *  rules accept, and every series date valid after any hand edits. */
function scheduleStepErrors(s: WorkshopDraftSchedule): StepErrors {
  const errs: StepErrors = { ...recurrenceFieldErrors(s) };
  if (!s.date) errs.startsAt = 'Date is required.';
  else if (isPastDate(s.date)) errs.startsAt = PAST_DATE_MESSAGE;
  if (!s.start) errs.start = 'Start time is required.';
  // The end is Start + the type's length; with a start, an empty end means the
  // workshop would run past midnight (the Workshop step already refused a type
  // with no length).
  else if (!s.end) errs.start = PAST_MIDNIGHT_MESSAGE;
  const problem = seriesProblem(s);
  if (problem) errs.recurrence = problem;
  const datesError = seriesDatesError(s);
  if (datesError) errs.seriesDates = datesError;
  return errs;
}

/** The error map for the VISIBLE step `step`. Always-4-step layout:
 *  0 Location · 1 Workshop · 2 Schedule · 3 Review. Review has no required
 *  fields → always {}. There is no Teacher step (BON-466). */
export function errorsForStep(step: number, draft: WorkshopDraft): StepErrors {
  if (step === 0) return locationStepErrors(draft);
  if (step === 1) return workshopStepErrors(draft.workshop);
  if (step === 2) return scheduleStepErrors(draft.schedule);
  return {};
}
